import React, { useState, useCallback } from 'react';
import { FiBarChart2, FiRefreshCw } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const METHOD_LABEL = {
  cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other',
};

const GROUP_OPTIONS = [
  { value: 'category',       label: 'Category-wise' },
  { value: 'payment_method', label: 'Payment Method-wise' },
  { value: 'date',           label: 'Date-wise' },
];

// Quick date-range presets
const getPreset = (preset) => {
  const now  = new Date();
  const pad  = (n) => String(n).padStart(2, '0');
  const ymd  = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (preset === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(from), to: ymd(now) };
  }
  if (preset === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to   = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: ymd(from), to: ymd(to) };
  }
  if (preset === 'this_fy') {
    const m     = now.getMonth() + 1;
    const start = m >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return { from: `${start}-04-01`, to: ymd(now) };
  }
  return { from: '', to: '' };
};

export default function AdminExpenseReports() {
  const [groupBy,    setGroupBy]   = useState('category');
  const [fromDate,   setFromDate]  = useState('');
  const [toDate,     setToDate]    = useState('');
  const [report,     setReport]    = useState(null);
  const [loading,    setLoading]   = useState(false);

  const applyPreset = (preset) => {
    const { from, to } = getPreset(preset);
    setFromDate(from);
    setToDate(to);
  };

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = { group_by: groupBy };
      if (fromDate) params.from_date = fromDate;
      if (toDate)   params.to_date   = toDate;
      const { data } = await API.get('/expenses/reports', { params });
      setReport(data);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [groupBy, fromDate, toDate]);

  const filterStyle = {
    padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
    fontSize: 13, background: '#fff', outline: 'none',
  };

  const maxVal = report?.rows?.length
    ? Math.max(...report.rows.map(r => r.total), 1)
    : 1;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiBarChart2 size={20} /> Expense Reports
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Covers CONFIRMED expenses only.
        </p>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>Group By</label>
            <select style={{ ...filterStyle }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>From</label>
            <input type="date" style={filterStyle} value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>To</label>
            <input type="date" style={filterStyle} value={toDate} onChange={e => setToDate(e.target.value)} />
          </div>

          {/* Presets */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {[
              { label: 'This Month', key: 'this_month' },
              { label: 'Last Month', key: 'last_month' },
              { label: 'This FY',    key: 'this_fy' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchReport}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: loading ? 0.7 : 1, marginLeft: 'auto' }}
          >
            <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading…' : 'Run Report'}
          </button>
        </div>
      </div>

      {/* Results */}
      {!report ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', fontSize: 14 }}>
          Select filters and click "Run Report" to see results.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          {/* Summary bar */}
          <div style={{ padding: '16px 24px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {GROUP_OPTIONS.find(o => o.value === report.group_by)?.label} &mdash; {report.rows?.length || 0} row(s)
              {fromDate || toDate ? ` · ${fromDate || '…'} to ${toDate || '…'}` : ' · All time'}
            </span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              Total: {money(report.grand_total)}
            </span>
          </div>

          {report.rows?.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
              No confirmed expenses found for this period.
            </div>
          ) : (
            <div style={{ padding: 24 }}>
              {report.rows.map((row, i) => {
                const label =
                  report.group_by === 'category'       ? row.category_name :
                  report.group_by === 'payment_method' ? (METHOD_LABEL[row.payment_method] || row.payment_method) :
                  fmt(row.date);

                const pct = (row.total / maxVal) * 100;

                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{label}</span>
                      <div style={{ display: 'flex', gap: 16, color: '#6b7280' }}>
                        <span>{row.count} expense{row.count !== 1 ? 's' : ''}</span>
                        <span style={{ fontWeight: 700, color: '#111', minWidth: 100, textAlign: 'right' }}>{money(row.total)}</span>
                      </div>
                    </div>
                    <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: 'var(--primary)', borderRadius: 4, transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
